import { Box, Paper, Skeleton, Text } from "@mantine/core"
import { ArrowDownIcon, ArrowUpIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { IDashboard } from "../../../models/Dashboard"
import {
  formatChange,
  formatCount,
  formatCurrencyShort,
  STAT_CONFIGS,
  StatConfig,
} from "../utils"
import classes from "../real.module.css"

interface Props {
  amounts: IDashboard["amounts"] | undefined
  isBuyer: boolean
}

export function KpiRow({ amounts, isBuyer }: Props) {
  return (
    <Box className={classes.kpiRow}>
      {amounts
        ? amounts.map((amount, i) => (
            <KpiCard key={i} amount={amount} config={STAT_CONFIGS[i] ?? STAT_CONFIGS[0]} isBuyer={isBuyer} />
          ))
        : [1, 2, 3, 4].map((i) => <Skeleton key={i} h={122} radius="md" />)}
    </Box>
  )
}

interface KpiCardProps {
  amount: NonNullable<IDashboard["amounts"]>[number]
  config: StatConfig
  isBuyer: boolean
}

function KpiCard({ amount, config, isBuyer }: KpiCardProps) {
  const { t } = useTranslation(["dashboard"])
  const change = formatChange(amount.changePct)
  const DiffIcon = change.isPositive ? ArrowUpIcon : ArrowDownIcon

  const currency = formatCurrencyShort(amount.amount || 0)

  return (
    <Paper className={classes.statCard}>
      <Text className={`${classes.statLbl} ${isBuyer ? classes.statLblBuyer : ""}`}>
        {t(`dashboard:${isBuyer && config.buyerLabelKey ? config.buyerLabelKey : config.labelKey}`)}
      </Text>
      <Text className={classes.statVal}>
        {config.isCurrency ? (
          <>
            {currency.num}
            <Text component="span" className={classes.statUnit}>{currency.unit}</Text>
          </>
        ) : (
          <>
            {formatCount(amount.amount || 0)}
            <Text component="span" className={classes.statUnit}>
              {t("dashboard:numberFormat.unit", { defaultValue: "ш" })}
            </Text>
          </>
        )}
      </Text>
      <Box className={classes.statChange}>
        {!change.isNew && !change.isUnknown && <DiffIcon size={14} weight="bold" />}
        {change.isNew || change.isUnknown
          ? change.label
          : `${change.label} ${t("dashboard:prevMonth")}`}
      </Box>
    </Paper>
  )
}
